import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import NearbyPhotographers from './components/NearbyPhotographers';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import Header from '../../components/layout/Header';

const DESKTOP_BREAKPOINT = 1024; // يطابق BOTTOM_NAV_MAX_WIDTH في Routes.jsx

const Landing = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { bannerUrl, mobileBannerUrl } = useBrand();

  // نُظهر هيدر Landing فقط على الجوال — على Desktop يتكفل به Routes.jsx
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= DESKTOP_BREAKPOINT : false
  );
  useEffect(() => {
    const check = () => setIsMobileViewport(window.innerWidth <= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const isPhotographer = userProfile?.role === 'photographer';

  const handleBookingClick = () => {
    if (isPhotographer) {
      navigate('/photographer-dashboard');
    } else {
      navigate('/booking-wizard');
    }
  };

  const categories = [
  {
    icon: 'Home',
    title: 'التصوير العقاري',
    description: 'أبرز جمال عقارك',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
  },
  {
    icon: 'User',
    title: 'التصوير الشخصي',
    description: 'صور احترافية تعكس شخصيتك',
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800&q=80'
  },
  {
    icon: 'Package',
    title: 'تصوير المنتجات',
    description: 'صور احترافية تبرز منتجاتك',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800&q=80'
  },
  {
    icon: 'UtensilsCrossed',
    title: 'تصوير الطعام',
    description: 'صور شهية تجذب الأنظار',
    image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80'
  }];

  const stats = [
  { 
    icon: 'Calendar', 
    title: 'حجز مرن', 
    description: 'في الوقت اللي يناسبك', 
    color: 'from-green-500 to-emerald-500',
    iconBg: 'bg-green-500/10',
  },
  { 
    icon: 'MapPin', 
    title: 'تغطية أوسع', 
    description: 'في مدن مختلفة داخل السعودية', 
    color: 'from-blue-500 to-cyan-500',
    iconBg: 'bg-blue-500/10',
  },
  { 
    icon: 'Camera', 
    title: 'مصورين معتمدين', 
    description: 'مؤهلين ومختارين بعناية', 
    color: 'from-blue-500 to-indigo-500',
    iconBg: 'bg-blue-500/10',
  },
  { 
    icon: 'Star', 
    title: 'تجربة متكاملة', 
    description: 'من الحجز إلى التسليم', 
    color: 'from-orange-500 to-amber-500',
    iconBg: 'bg-orange-500/10',
  }];


  // Bento Grid — 6 صور متنوعة مناسبة للسوق السعودي
  const showcaseImages = [
    {
      src: 'https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'تصوير عقاري داخلي فاخر',
      className: 'col-span-1 md:col-span-2 h-56 md:h-72',
    },
    {
      src: 'https://images.unsplash.com/photo-1755412734479-c0687daab775?q=80&w=880&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'تصوير بورتريه احترافي',
      className: 'col-span-1 h-56 md:h-72',
    },
    {
      src: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?q=80&w=1299&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'تصوير طعام احترافي',
      className: 'col-span-1 h-48 md:h-56',
    },
    {
      src: 'https://images.unsplash.com/photo-1722942723829-eb13e2cbc67b?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'تصوير جوي للمدينة بالدرون',
      className: 'col-span-1 h-48 md:h-56',
    },
    {
      src: 'https://images.unsplash.com/photo-1617661338085-d1ec6a89a6d8?q=80&w=1374&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'تصوير منتجات فاخرة',
      className: 'col-span-1 h-48 md:h-56',
    },
    {
      src: 'https://images.pexels.com/photos/4321707/pexels-photo-4321707.jpeg',
      alt: 'تصوير حفل رسمي وفعاليات',
      className: 'col-span-2 md:col-span-3 h-48 md:h-52',
    },
  ];

  // الاعتماد على ألوان الهوية البصرية من لوحة التحكم (تُحقن كـ CSS variables عبر BrandContext)
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* هيدر Landing: يُحمَّل فقط على الجوال (JS conditional لا CSS) — على Desktop يتكفل Routes.jsx */}
      {isMobileViewport && <Header />}
      <main className="pt-[calc(4rem+var(--app-download-banner-height,0px))] lg:pt-0">
        {/* Hero: صورة البانر من الهوية البصرية فقط — إن لم يرفع المشرف بانراً نعرض تدرجاً فقط */}
        <div className="relative h-[650px] md:h-[750px] overflow-hidden">
          <div className="absolute inset-0">
            {/* بانر سطح المكتب — يظهر فقط على md وما فوق */}
            {bannerUrl ? (
              <Image
                src={bannerUrl}
                alt="بانر الصفحة الرئيسية"
                className="hidden md:block w-full h-full object-cover"
              />
            ) : null}
            {/* بانر الجوال — يظهر فقط على شاشات أصغر من md، يرجع للبانر الرئيسي إن لم يوجد بانر جوال */}
            {(mobileBannerUrl || bannerUrl) ? (
              <Image
                src={mobileBannerUrl || bannerUrl}
                alt="بانر الجوال"
                className="block md:hidden w-full h-full object-cover object-top"
              />
            ) : null}
            {/* طبقة التعتيم / التدرج — بألوان الهوية البصرية */}
            <div
              className="absolute inset-0"
              style={{
                background: bannerUrl
                  ? 'linear-gradient(to bottom right, rgba(15,23,42,0.80), var(--color-primary)99, rgba(15,23,42,0.60))'
                  : 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 50%, var(--color-primary) 100%)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at top right, var(--color-secondary)4D, transparent 50%), radial-gradient(circle at bottom left, var(--color-primary)4D, transparent 50%)',
              }}
            />
          </div>
          
          <div className="relative h-full flex items-center">
            <div className="container mx-auto px-4 md:px-6 lg:px-8">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 px-2.5 py-1 md:px-3 md:py-1.5 bg-white/90 backdrop-blur-md rounded-full" style={{ background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #c66e06, #ff8a00) border-box', border: '1.5px solid transparent' }}>
                  <span className="text-[10px] md:text-xs font-medium" style={{ background: 'linear-gradient(90deg, #c66e06, #ff8a00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>✨ منصة التصوير الأولى في المملكة</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: 'var(--color-foreground)' }}>
                احجز مصورك
                  <span
                    className="block text-transparent bg-clip-text text-2xl md:text-3xl lg:text-4xl"
                    style={{ backgroundImage: 'linear-gradient(to right, var(--color-primary), color-mix(in srgb, var(--color-primary) 60%, var(--color-secondary)), var(--color-secondary))' }}
                  >
                    بسهولة
                  </span>
                </h1>
                <p className="text-lg md:text-xl mb-10 leading-relaxed" style={{ color: 'var(--color-foreground)', opacity: 0.7 }}>
                كل اللي تحتاجه لجلسة تصوير إحترافية
                </p>
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Button
                    size="xl"
                    onClick={handleBookingClick}
                    className="text-white px-12 py-7 text-lg shadow-2xl transition-all duration-300 transform hover:scale-105 w-auto"
                    style={{
                      background: 'linear-gradient(to left, var(--color-hero-cta-gradient-start), var(--color-hero-cta-gradient-end))',
                      boxShadow: '0 25px 50px -12px color-mix(in srgb, var(--color-hero-cta-gradient-start) 50%, transparent)',
                    }}>
                    {isPhotographer ? 'لوحة التحكم' : 'ابدأ الحجز الآن'}
                  </Button>
                  <Button
                    size="xl"
                    onClick={() => navigate('/photographer-register')}
                    className="px-12 py-7 text-lg transition-all duration-300 transform hover:scale-105 w-auto"
                    style={{
                      background: 'transparent',
                      border: '2px solid #7B68EE',
                      color: '#7B68EE',
                    }}>
                    إنضم كمصور
                    <Icon name="Camera" size={20} className="mr-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Elements — ألوان الهوية البصرية */}
          <div className="absolute bottom-10 left-10 w-20 h-20 rounded-full blur-2xl opacity-60 animate-pulse" style={{ background: 'linear-gradient(to bottom right, var(--color-secondary), color-mix(in srgb, var(--color-secondary) 80%, transparent))' }}></div>
          <div className="absolute top-20 right-20 w-32 h-32 rounded-full blur-3xl opacity-40 animate-pulse" style={{ animationDelay: '1s', background: 'linear-gradient(to bottom right, var(--color-primary), color-mix(in srgb, var(--color-primary) 80%, transparent))' }}></div>
        </div>

        {/* Modern Stats Section */}
        <div className="relative -mt-20 z-10">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 lg:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {stats?.map((stat, index) =>
                <div key={index} className="text-center group cursor-pointer p-4 md:p-6 rounded-2xl hover:bg-slate-50 transition-all duration-300">
                    <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 md:mb-5 rounded-2xl bg-gradient-to-br ${stat?.color} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                      <Icon name={stat?.icon} size={24} className="md:w-7 md:h-7 text-white" />
                    </div>
                    <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 mb-2 md:mb-3 leading-tight">
                      {stat?.title}
                    </h3>
                    <p className="text-sm md:text-base text-slate-600 font-medium leading-relaxed max-w-xs mx-auto">
                      {stat?.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Nearby Photographers Section */}
        <NearbyPhotographers />

        {/* Showcase Gallery Section */}
        <div className="py-24">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 rounded-full" style={{ background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #c66e06, #ff8a00) border-box', border: '1.5px solid transparent' }}>
                <span className="text-sm font-semibold" style={{ background: 'linear-gradient(90deg, #c66e06, #ff8a00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>مشاهد مختارة</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                تميز في كل لقطة
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                مصورين مميزين لتغطية كامل إحتياجاتك
              </p>
            </div>

            {/* صورة كبيرة + 4 صور جانبية */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="col-span-2 md:col-span-2 row-span-2 relative rounded-2xl overflow-hidden h-64 md:h-[34rem] shadow-lg">
                <Image
                  src={showcaseImages[0].src}
                  alt={showcaseImages[0].alt}
                  loading="eager"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              {showcaseImages.slice(1, 5).map((img, index) => (
                <div key={index} className="relative rounded-2xl overflow-hidden h-32 md:h-[16.5rem] shadow-md">
                  <Image
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>

            {/* الصورة السادسة — عرض كامل أسفل الجريد */}
            <div className="relative rounded-2xl overflow-hidden h-48 md:h-64 shadow-md mt-3">
              <Image
                src={showcaseImages[5].src}
                alt={showcaseImages[5].alt}
                loading="lazy"
                className="w-full h-full object-cover object-bottom hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="py-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-block mb-4 px-4 py-2 rounded-full" style={{ background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #c66e06, #ff8a00) border-box', border: '1.5px solid transparent' }}>
                <span className="text-sm font-semibold" style={{ background: 'linear-gradient(90deg, #c66e06, #ff8a00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>كيف نعمل؟</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                في خطوات بسيطة
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 15%, transparent)' }}>
                  <Icon name="Search" size={18} style={{ color: 'var(--color-primary)' }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">1. اختر احتياجاتك</h3>
                <p className="text-slate-600">حدد نوع التصوير والتفاصيل المطلوبة</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 15%, transparent)' }}>
                  <Icon name="Users" size={18} style={{ color: 'var(--color-primary)' }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">2. اختر المصور</h3>
                <p className="text-slate-600">تصفح المصورين المتاحين واختر الأنسب</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary) 15%, transparent)' }}>
                  <Icon name="Calendar" size={18} style={{ color: 'var(--color-primary)' }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">3. احجز واستمتع</h3>
                <p className="text-slate-600">أكمل الحجز واحصل على صور احترافية</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div
              className="relative rounded-3xl p-12 text-center overflow-hidden backdrop-blur-sm"
              style={{
                background: 'color-mix(in srgb, var(--color-primary) 6%, transparent)',
                border: '1.5px solid color-mix(in srgb, var(--color-primary) 30%, transparent)',
                boxShadow: '0 0 60px -10px color-mix(in srgb, var(--color-primary) 15%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-primary) 20%, white)',
              }}
            >
              {/* توهج علوي ناعم */}
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-40 blur-3xl opacity-30 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse, var(--color-primary), transparent 70%)' }}
              />
              <div className="relative z-10">
                <p className="text-sm font-semibold mb-4" style={{ background: 'linear-gradient(90deg, #c66e06, #ff8a00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  ابدأ الآن
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  جاهز لحجز جلستك؟
                </h2>
                <p className="text-lg text-slate-500 mb-10 max-w-xl mx-auto">
                احجز مصورك خلال دقائق، والباقي علينا
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="xl"
                    onClick={handleBookingClick}
                    className="text-white px-12 py-7 text-lg transition-all duration-300 transform hover:scale-105"
                    style={{
                      background: 'linear-gradient(to left, var(--color-hero-cta-gradient-start), var(--color-hero-cta-gradient-end))',
                      boxShadow: '0 20px 40px -12px color-mix(in srgb, var(--color-hero-cta-gradient-start) 50%, transparent)',
                    }}>
                    {isPhotographer ? 'لوحة التحكم' : 'احجز الآن'}
                  </Button>
                  <Button
                    size="xl"
                    onClick={() => navigate('/photographer-register')}
                    className="px-12 py-7 text-lg transition-all duration-300 transform hover:scale-105 bg-transparent hover:bg-white/60"
                    style={{
                      border: '1.5px solid color-mix(in srgb, var(--color-primary) 40%, transparent)',
                      color: 'var(--color-primary)',
                    }}>
                    <Icon name="Camera" size={20} className="ml-2" />
                    إنضم كمصور
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default Landing;
